/**
 * Публичные формулы игрового ряда (п. 4.2 ТЗ). Формула определяет, как из минутных баров
 * компонентов получается бар, который видят игроки.
 *
 * «EW3-v1» — равновзвешенный индекс трёх скрытых акций (режим «Слепая тройка»):
 *   I(t) = I₀ · (1 + (1/3) · Σᵢ (pᵢ(t) / Bᵢ − 1))
 *   Bᵢ  — цена закрытия акции i на баре P₀ (общая база в момент начала розыгрыша);
 *   I₀  = 5000,00 пунктов — значение индекса на закрытии бара P₀, т.е. P₀ = I₀ всегда;
 *   шаг цены 0,25 пункта. Абсолютный уровень цен акций не раскрывается.
 *
 * «PX1-v1» — цена одного известного инструмента без нормировки (режим «Открытые карты»):
 *   I(t) = p(t), шаг 0,01 (цент). P₀ — фактическая цена закрытия бара P₀.
 *
 * Общее: вычисления в двойной точности IEEE-754; округление до ближайшего шага, половина — вверх.
 *
 * OHLC бара агрегата:
 *   O = I(открытия), C = I(закрытия);
 *   H = max(I(максимумы), O, C), L = min(I(минимумы), O, C) — верхняя/нижняя граница,
 *   т.к. максимумы компонентов могли достигаться в разные моменты минуты.
 *
 * Пропуски: если у акции нет бара в минуте (не было сделок), используется последняя
 * известная цена закрытия (O = H = L = C = предыдущее закрытие, объём 0).
 *
 * Активность (вместо объёма): v = (1/n) · Σᵢ volᵢ(t) / avgVolᵢ, где avgVolᵢ — средний объём
 * акции по окну истории розыгрыша. Безразмерная величина (1 = обычная активность);
 * абсолютные объёмы отдельных акций не раскрываются.
 */
/** Минутный бар одной акции, время — начало минуты (UTC, мс). */
export interface MinuteBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export const MINUTE = 60_000;

/** Описание формулы ряда. */
export interface IndexFormula {
  id: string;
  /** число компонентов */
  components: number;
  /** значение ряда на P₀; null — цена инструмента без нормировки */
  base: number | null;
  /** шаг цены */
  tick: number;
}

export const EW3: IndexFormula = { id: 'EW3-v1', components: 3, base: 5000, tick: 0.25 };
export const PX1: IndexFormula = { id: 'PX1-v1', components: 1, base: null, tick: 0.01 };
export const FORMULAS: Readonly<Record<string, IndexFormula>> = { [EW3.id]: EW3, [PX1.id]: PX1 };

export function formulaById(id: string): IndexFormula | undefined {
  return FORMULAS[id];
}

/** Формула по умолчанию (исторически единственная). */
export const FORMULA_ID = EW3.id;
export const INDEX_BASE = 5000;
export const TICK = EW3.tick;

export function roundToTick(x: number, tick = TICK): number {
  const ticks = Math.floor(x / tick + 0.5 + 1e-9);
  return Math.round(ticks * tick * 100) / 100;
}

/** Значение ряда по ценам компонентов. base = null — цена единственного компонента. */
export function indexValue(prices: number[], bases: number[], base: number | null = INDEX_BASE): number {
  if (base === null) return prices[0]!;
  let sum = 0;
  for (let i = 0; i < prices.length; i++) sum += prices[i]! / bases[i]! - 1;
  return base * (1 + sum / prices.length);
}

/** Значение ряда на P₀ (закрытие бара P₀). */
export function p0Value(f: IndexFormula, basePrices: number[]): number {
  return f.base === null ? roundToTick(basePrices[0]!, f.tick) : f.base;
}

export interface AggregateBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export function aggregateBar(t: number, comps: MinuteBar[], bases: number[], avgVol: number[], f: IndexFormula = EW3): AggregateBar {
  const at = (pick: (b: MinuteBar) => number) => roundToTick(indexValue(comps.map(pick), bases, f.base), f.tick);
  const o = at((b) => b.o);
  const c = at((b) => b.c);
  const h = Math.max(at((b) => b.h), o, c);
  const l = Math.min(at((b) => b.l), o, c);
  let act = 0;
  for (let i = 0; i < comps.length; i++) act += comps[i]!.v / Math.max(1, avgVol[i]!);
  const v = Math.round((act / comps.length) * 100) / 100;
  return { t, o, h, l, c, v };
}
